import React from 'react';
import { Card, Tag, Space, Button, Alert } from 'antd';
import {
  TrophyOutlined,
  CrownOutlined,
  HeartOutlined,
  CarOutlined,
  SearchOutlined,
  BarChartOutlined,
  SettingOutlined,
  CalculatorOutlined,
  FunctionOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { AppSettings } from '../types';

interface Props {
  settings: AppSettings;
  announcement?: string;
  onNavigate?: (key: string) => void;
}

// 功能导航卡片与列表已移除

const NavigationGuide: React.FC<Props> = ({ onNavigate, announcement }) => {
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="使用指南 / 导航">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* 公告与重要说明（顶部固定高度可滚动） */}
          <Card size="small" title="公告">
            <div style={{ maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap', color: '#444' }}>
              {announcement || '暂无公告'}
            </div>
          </Card>

          <Alert
            type="warning"
            showIcon
            message="重要说明"
            description={
              <div>
                本系统仅用于数据展示和学习研究，切勿用于商业或违法用途。如内容侵犯您的合法权益，请通过邮箱 s1145143104358@outlook.com 联系，我们将尽快处理。问题反馈可加群 513291473 或发送邮件。
              </div>
            }
          />

          <Card size="small" title="网站总体介绍">
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>榜单中心：战力、宠物、坐骑、神魔、联盟等多个排行榜，支持历史快照浏览与两期对比，提供“新上榜”标记。</li>
              <li>历史体系：按日/周/月份粒度采集快照，默认选择最新两期进行对比并自动处理日期排序问题。</li>
              <li>分析工具：六维计算器与 PVE/PVP 伤害模拟，用于数值评估与策略比较。</li>
              <li>系统详情：角色各系统战力与明细信息的查询与更新，采用统一接口格式。</li>
              <li>辅助能力：全局搜索、统计分析、系统设置（区服/页大小/单位显示）。</li>
            </ul>
          </Card>

          <Alert
            type="info"
            showIcon
            message="历史快照与对比"
            description={
              <div>
                <div>支持历史的页面右上角都有“历史/对比”控件：</div>
                <ul style={{ paddingLeft: 18, margin: '8px 0' }}>
                  <li>单期浏览：选择一个快照显示该期榜单；默认取最新一期。</li>
                  <li>两期对比：默认取最近两期（起点=上一期，目标=最新），展示分数与排名变化。</li>
                  <li>新上榜：若起点缺失而目标存在，行内显示<Tag color="green">新上榜</Tag> 标记。</li>
                  <li>排序：内部按真实时间排序，兼容非零填充日期（如 2025-8-6）。</li>
                </ul>
              </div>
            }
          />

          <Alert
            type="success"
            showIcon
            message="对比口径"
            description={
              <div>
                <ul style={{ paddingLeft: 18, margin: '8px 0' }}>
                  <li>分数变化 = 目标期 − 起点期；排名变化 = 目标期 − 起点期（负数=上升）。</li>
                  <li>联盟对比以联盟总战力比对；神魔采用全服排名字段；角色榜不提供历史对比。</li>
                  <li>若某期缺失字段：分数/排名变化会按同字段回退或显示 0</li>
                </ul>
              </div>
            }
          />
        </Space>
      </Card>

      <Card title="各页面详解">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card size="small" title={<Space><TrophyOutlined />战力排行榜</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>主要字段：上榜战力、当前战力、等级、角色、联盟、地区、UID、区服、排名。</li>
              <li>筛选：右上“角色”下拉；历史：单期/对比（支持“新上榜”标记）。</li>
              <li>排名变化：负数=上升，正数=下降，0=持平；提供可视化上升/下降指示。</li>
              <li>排序：按上榜战力排序</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('power')}>前往战力榜</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><HeartOutlined />宠物排行榜</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>主要字段：宠物种类、分数、等级、昵称、主人、皮肤、UID、区服、排名。</li>
              <li>筛选：右上选择宠物种类；历史：单期/对比，支持“新上榜”。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('pet')}>前往宠物榜</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><CarOutlined />坐骑排行榜</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>主要字段：坐骑种类、分数、等级、昵称、主人、皮肤、UID、区服、排名。</li>
              <li>筛选：右上选择坐骑种类；历史：单期/对比，支持“新上榜”。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('ride')}>前往坐骑榜</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><ThunderboltOutlined />神魔排行榜（神将/魔王）</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>切换类型：使用上方分段选择 神将/魔王；可按角色过滤。</li>
              <li>主要字段：全服排名、玩家、战力、分数、UID、角色、联盟、地区。</li>
              <li>历史：单期/对比，显示分数/排名变化与“新上榜”。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('godwar')}>前往神魔榜</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><CrownOutlined />联盟排行榜</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>主要字段：等级、总战力、资金、人数/上限、盟主、宣言、区服、排名。</li>
              <li>历史对比：分数变化使用联盟总战</li>
              <li>宣言查看：点击书本图标弹出全文。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('league')}>前往联盟榜</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><SearchOutlined />全局搜索</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>输入玩家名/UID/联盟名等关键字，跨榜单检索。</li>
              <li>支持部分匹配与区服限定（结合系统设置的区服）。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('search')}>前往搜索</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><CalculatorOutlined />六维计算器 / 伤害模拟</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>用途：方便看出自身角色属性对战斗表现的影响，便于对自身属性进行评估</li>
              <li>使用uid获取属性需要先去属性更新页面将该uid加入服务器数据中</li>
              <li>PVE 对战：输入或者使用uid直接获取填入我方属性,敌方属性相同操作</li>
              <li>PVP 对战: 相较于PVE对战增加了对战属性,但是使用uid获取不到对战属性,需要自己进行手动输入,其他操作同PVE</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('calculator')}>前往计算器</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><FunctionOutlined />角色各系统详情</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>用途：展示角色各个系统的战力详情,包括装备基础
装备附加
宝石属性
装备套装
时装属性
内丹属性
称号属性
法宝属性
翅膀属性
羽毛属性
炼体属性
阵法属性
星核属性
外丹属性
熔炼属性
</li>
              <li>查询：通过 UID 获取各项系统战力详情</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('systempower')}>前往系统详情</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><BarChartOutlined />统计分析</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>展示分布与概览（如宠物/坐骑分布、人数统计等），此项不会再额外添加内容</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('statistics')}>前往统计</Button>
            </div>
          </Card>

          <Card size="small" title={<Space><SettingOutlined />系统设置</Space>}>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>页大小、区服、单位（万/亿等）会影响所有榜单；设置保存在本地浏览器。</li>
              <li>问题反馈通过此界面进行</li>
              <li>支持一键清除所有设置，恢复默认值。</li>
              <li>移动端将默认收起侧边栏以提升可用空间。</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => onNavigate?.('settings')}>前往设置</Button>
            </div>
          </Card>
        </Space>
      </Card>

      {/* 功能导航部分已删除 */}
    </Space>
  );
};

export default NavigationGuide;
